import { cn, getApiBase } from '../../../lib/utils';
import type { AgentContact } from '../types/messenger.types';

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();

    if (isToday) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    if (isYesterday) return 'Hier';
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
}

function AgentAvatar({ agent }: { agent: AgentContact }) {
  const iconUrl = agent.icon_preview_url || agent.icon_url;
  const absoluteUrl = iconUrl
    ? (iconUrl.startsWith('http') ? iconUrl : `${getApiBase()}${iconUrl}`)
    : null;
  const initial = agent.name.charAt(0).toUpperCase();

  return (
    <div className="relative shrink-0">
      {absoluteUrl ? (
        <img
          src={absoluteUrl}
          alt={agent.name}
          className="h-12 w-12 rounded-full object-cover bg-zinc-800"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      ) : (
        <div className="h-12 w-12 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
          <span className="text-emerald-400 font-semibold text-lg">{initial}</span>
        </div>
      )}
      <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-emerald-500 border-2 border-zinc-950" />
    </div>
  );
}

interface ContactCardProps {
  agent: AgentContact;
  onClick: () => void;
  isActive?: boolean;
}

export function ContactCard({ agent, onClick, isActive }: ContactCardProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
        isActive ? 'bg-emerald-500/10' : 'hover:bg-zinc-800/60'
      )}
    >
      <AgentAvatar agent={agent} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-white text-sm truncate">{agent.name}</span>
          {agent.lastMessage && (
            <span className="text-[10px] text-zinc-500 shrink-0">
              {formatTime(agent.lastMessage.timestamp)}
            </span>
          )}
        </div>
        <p className="text-xs text-zinc-400 truncate mt-0.5">
          {agent.lastMessage
            ? `${agent.lastMessage.isFromAgent ? '' : 'Vous : '}${agent.lastMessage.content}`
            : agent.description || 'Aucun message'}
        </p>
      </div>

      {agent.unreadCount > 0 && (
        <span className="shrink-0 min-w-[20px] h-5 rounded-full bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
          {agent.unreadCount > 99 ? '99+' : agent.unreadCount}
        </span>
      )}
    </button>
  );
}
